import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from "../auth/tokenStorage";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";
const REFRESH_ENDPOINT =
  process.env.REACT_APP_AUTH_REFRESH_ENDPOINT || "/api/auth/refresh";

let refreshPromise = null;
let authFailureHandler = null;

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function setAuthFailureHandler(handler) {
  authFailureHandler = handler;
}

export function buildApiUrl(path) {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

async function parseResponseData(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

function normalizeBody(body, headers) {
  if (!body || body instanceof FormData || typeof body === "string") {
    return body;
  }

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return JSON.stringify(body);
}

function resolveErrorMessage(responseData, fallbackMessage) {
  if (!responseData || typeof responseData !== "object") {
    return fallbackMessage;
  }

  return (
    responseData.message ||
    responseData.resultMessage ||
    responseData.errorMessage ||
    responseData.error?.message ||
    fallbackMessage
  );
}

function triggerAuthFailure() {
  if (authFailureHandler) {
    authFailureHandler();
  } else {
    window.location.replace("/auth/login");
  }
}

async function requestTokenRefresh() {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new ApiError("리프레시 토큰이 없습니다.", 401);
  }

  const response = await fetch(buildApiUrl(REFRESH_ENDPOINT), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken }),
  });

  const responseData = await parseResponseData(response);

  if (!response.ok) {
    throw new ApiError(
      resolveErrorMessage(responseData, "토큰 재발급에 실패했습니다."),
      response.status,
      responseData,
    );
  }

  if (!responseData?.accessToken || !responseData?.refreshToken) {
    throw new ApiError("재발급 응답에 토큰이 없습니다.", 401, responseData);
  }

  setAuthTokens({
    accessToken: responseData.accessToken,
    refreshToken: responseData.refreshToken,
  });

  return responseData.accessToken;
}

async function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    refreshPromise = requestTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function executeRequest(path, options, accessToken, requiresAuth) {
  const headers = new Headers(options.headers || {});
  const body = normalizeBody(options.body, headers);

  if (requiresAuth && accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  return fetch(buildApiUrl(path), {
    ...options,
    headers,
    body,
  });
}

export async function apiFetch(path, options = {}, config = {}) {
  const { auth = true, retryOnUnauthorized = true } = config;

  let response = await executeRequest(path, options, getAccessToken(), auth);

  if (response.status === 401 && auth && retryOnUnauthorized) {
    try {
      const refreshedAccessToken = await refreshAccessTokenOnce();
      response = await executeRequest(
        path,
        options,
        refreshedAccessToken,
        auth,
      );
    } catch (error) {
      clearAuthTokens();
      triggerAuthFailure();
      throw error;
    }
  }

  const responseData = await parseResponseData(response);

  if (!response.ok) {
    const message = resolveErrorMessage(
      responseData,
      `요청에 실패했습니다. (${response.status})`,
    );
    throw new ApiError(message, response.status, responseData);
  }

  return responseData;
}

export async function publicApiFetch(path, options = {}) {
  return apiFetch(path, options, { auth: false, retryOnUnauthorized: false });
}
